// File: frontend/src/pages/exams/ResultList.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../utils/axios';

const ResultList = () => {
  const { examId } = useParams();

  const [exam, setExam] = useState(null);
  const [resultsData, setResultsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch results list for selected exam and group per student
   */
  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/exams/results/${examId}`);
      if (response.data?.success) {
        setExam(response.data.data?.exam || null);
        const rawResults = response.data.data?.results || [];
        setResultsData(rawResults);
      } else {
        setError(response.data?.message || 'Failed to load examination results.');
      }
    } catch (err) {
      console.error('Error fetching exam results:', err);
      setError(err.response?.data?.message || err.message || 'Error loading exam results.');
    } finally {
      setLoading(false);
    }
  }, [examId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  /**
   * Process raw results into student rows with subject columns & summaries
   */
  const processStudentRows = () => {
    const studentMap = {};

    resultsData.forEach((row) => {
      const sId = row.student_id;
      if (!studentMap[sId]) {
        studentMap[sId] = {
          student_id: sId,
          student_name: row.student_name,
          roll_no: row.roll_no,
          subjects: {},
          totalObtained: 0,
          totalMax: 0,
          hasFailedSubject: false
        };
      }

      const obtained = parseFloat(row.marks_obtained) || 0;
      const max = parseFloat(row.total_marks) || 100;
      const pass = parseFloat(row.pass_marks) || 40;

      studentMap[sId].subjects[row.subject_name] = {
        obtained,
        max,
        grade: row.grade
      };

      studentMap[sId].totalObtained += obtained;
      studentMap[sId].totalMax += max;

      if (obtained < pass) {
        studentMap[sId].hasFailedSubject = true;
      }
    });

    return Object.values(studentMap).map((st) => {
      const percentage = st.totalMax > 0 ? (st.totalObtained / st.totalMax) * 100 : 0;
      let finalGrade = 'F';
      if (percentage >= 90) finalGrade = 'A+';
      else if (percentage >= 80) finalGrade = 'A';
      else if (percentage >= 70) finalGrade = 'B';
      else if (percentage >= 60) finalGrade = 'C';
      else if (percentage >= 50) finalGrade = 'D';

      const isPassed = !st.hasFailedSubject && percentage >= 40;

      return {
        ...st,
        percentage: parseFloat(percentage.toFixed(2)),
        finalGrade,
        status: isPassed ? 'PASS' : 'FAIL'
      };
    });
  };

  // Extract distinct subject names for dynamic table columns
  const distinctSubjects = Array.from(
    new Set(resultsData.map((r) => r.subject_name))
  ).sort();

  const studentRows = processStudentRows();

  const handleExportCSV = () => {
    if (!studentRows || studentRows.length === 0) return;
    const headers = ['Roll No', 'Student Name', 'Total Obtained', 'Max Marks', 'Percentage', 'Overall Grade'];
    const rows = studentRows.map(s => [
      s.roll_no || s.student_id,
      `"${(s.student_name || '').replace(/"/g, '""')}"`,
      s.totalObtained,
      s.totalMax,
      `${s.percentage.toFixed(1)}%`,
      s.overallGrade
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${(exam?.name || 'Exam').replace(/\s+/g, '_')}_Results.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/exams" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">
                {exam ? `${exam.name} Results` : 'Examination Results'}
              </h1>
              <p className="text-xs text-slate-500">
                {exam ? `Class: ${exam.class_name || `Class ${exam.class_id}`} • Term: ${exam.term}` : 'Student Marks Overview'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleExportCSV}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-4 py-2 rounded-xl text-sm transition"
            >
              Export CSV
            </button>
            {examId && (
              <Link
                to={`/exams/enter/${examId}`}
                className="bg-primary hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded-xl text-sm transition shadow-sm"
              >
                + Enter Marks
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {/* Results Overview Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 text-base">Class Results Summary Sheet</h2>
            <span className="text-xs text-slate-500">Total Students: {studentRows.length}</span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400 font-medium">Loading class result sheet...</div>
          ) : studentRows.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium">
              No results recorded for this examination yet. Click "+ Enter Marks" to record results.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-sans">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                    <th className="py-3.5 px-4">Roll No</th>
                    <th className="py-3.5 px-6">Student Name</th>
                    {distinctSubjects.map((subName) => (
                      <th key={subName} className="py-3.5 px-4 text-center">{subName}</th>
                    ))}
                    <th className="py-3.5 px-4 text-right">Total Marks</th>
                    <th className="py-3.5 px-4 text-right">%</th>
                    <th className="py-3.5 px-4 text-center">Grade</th>
                    <th className="py-3.5 px-4 text-center">Status</th>
                    <th className="py-3.5 px-6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {studentRows.map((st) => (
                    <tr key={st.student_id} className="hover:bg-slate-50/80 transition">
                      <td className="py-4 px-4 font-mono font-bold text-slate-800">
                        {st.roll_no || st.student_id}
                      </td>
                      <td className="py-4 px-6 font-bold text-slate-900 text-sm">
                        {st.student_name}
                      </td>

                      {distinctSubjects.map((subName) => {
                        const subData = st.subjects[subName];
                        return (
                          <td key={subName} className="py-4 px-4 text-center font-mono font-semibold">
                            {subData ? (
                              <span>{subData.obtained} <span className="text-[10px] text-slate-400">({subData.grade})</span></span>
                            ) : (
                              <span className="text-slate-300">-</span>
                            )}
                          </td>
                        );
                      })}

                      <td className="py-4 px-4 text-right font-mono font-bold text-slate-900">
                        {st.totalObtained} / {st.totalMax}
                      </td>

                      <td className="py-4 px-4 text-right font-mono font-bold text-primary">
                        {st.percentage}%
                      </td>

                      <td className="py-4 px-4 text-center font-bold">
                        <span className="px-2.5 py-1 bg-slate-100 rounded-lg text-slate-800 border">
                          {st.finalGrade}
                        </span>
                      </td>

                      <td className="py-4 px-4 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${
                          st.status === 'PASS'
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                            : 'bg-red-100 text-red-800 border border-red-300'
                        }`}>
                          {st.status}
                        </span>
                      </td>

                      <td className="py-4 px-6 text-right">
                        <Link
                          to={`/exams/reportcard/${st.student_id}/${examId}`}
                          className="bg-primary/10 hover:bg-primary/20 text-primary font-bold px-3 py-1.5 rounded-lg transition text-xs inline-block"
                        >
                          Report Card
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default ResultList;
